// frontend/src/components/dashboard/DailyBriefToast.js
//
// The "first sign-in of the day" AI overview — a user-dismissible briefing
// that appears once, the first time a manager opens the dashboard on a
// given calendar day, with a short AI-written summary of where the
// business stands.
//
// "Once per day" is enforced in two layers:
// 1. Here, via localStorage keyed by email + today's date — the only
//    layer that actually stops it from popping up again on every page
//    reload within the same day, since that's a per-browser decision
//    this component is the only place that can make.
// 2. Server-side (utils/aiBriefCache.js) — one Groq call per BUSINESS
//    per day, not per person, so this only saves an AI call if two
//    people/browsers happen to hit it the same day; it does nothing to
//    stop this component's own re-renders, which is why layer 1 still
//    exists and does the real gating.
//
// Deliberately fails silently: if the plan doesn't include AI, the AI
// call errors, or anything else goes wrong, this just renders nothing
// — a missing "good morning" toast is a non-event, not something worth
// interrupting anyone with an error message over.
import React, { useEffect, useState } from 'react';
import * as api from '../../api';

function todayStorageKey(email) {
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
  return `dailyBrief:${email || 'anon'}:${today}`;
}

function greeting() {
  const hour = new Date().getHours();
  if (hour < 12) return "Good morning";
  if (hour < 18) return "Good afternoon";
  return "Good evening";
}

export default function DailyBriefToast({ user, dashboardData, pendingExpenses, pendingRestocks, lowStockCount }) {
  const [brief, setBrief] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    if (!user?.email || !dashboardData) return;
    const key = todayStorageKey(user.email);
    let alreadyShownToday = false;
    try { alreadyShownToday = !!localStorage.getItem(key); } catch (e) { /* localStorage unavailable — just skip the toast rather than crash */ return; }
    if (alreadyShownToday) return;

    let cancelled = false;
    api.aiDailyBrief({ dashboardData, pendingExpenses, pendingRestocks, lowStockCount })
      .then(res => {
        if (cancelled || !res?.brief) return;
        setBrief(res.brief);
        try { localStorage.setItem(key, '1'); } catch (e) { /* non-fatal — worst case it shows again next reload today */ }
      })
      .catch(() => { /* silent — see file header */ });

    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.email, !!dashboardData]);

  if (!brief || dismissed) return null;

  return (
    <div className="daily-brief-toast" role="status">
      <div className="daily-brief-toast-head">
        <span className="daily-brief-toast-icon">✨</span>
        <span className="daily-brief-toast-title">{greeting()} — Today's Overview</span>
        <button className="daily-brief-toast-close" onClick={() => setDismissed(true)} aria-label="Dismiss">✕</button>
      </div>
      <p className="daily-brief-toast-body">{brief}</p>
    </div>
  );
}
